import React, { useRef, useState } from "react";
import Layout from 'Layout'
import { Pagination } from "antd";
import './styles.scss'
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Dropdown,
  Breadcrumb,
  message,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import { useNavigate } from "react-router-dom";
import {
  SearchOutlined,
  ExportOutlined,
  MoreOutlined,
  EyeOutlined,
  DeleteOutlined,
  HomeOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { FilterDropdownProps } from "antd/es/table/interface";

const { Option } = Select;

interface Partner {
  key: string;
  name: string;
  price: number;
  hoahong: number;
  status: boolean;
  affiliateCode: string;
  affiliateLink: number;
  affiliateAll: number;
  commission: number;
  revenue: string;
  createdAt: string;
}
const sampleData: Partner[] = [
  {
    key: "1",
    name: "Nguyễn Văn A",
    price: 1000000,
    hoahong: 12,
    status: true,
    affiliateCode: '2 tháng',
    affiliateLink: 5,
    affiliateAll: 7,
    commission: 120000,
    revenue: '13/08/2025',
    createdAt: '2025-08-13',
  },
  {
    key: "2",
    name: "Trần Thị B",
    price: 987654321,
    hoahong: 13,
    status: false,
    affiliateCode: '1 tháng',
    affiliateLink: 1,
    affiliateAll: 1,
    commission: 0,
    revenue: '06/08/2025',
    createdAt: '2025-08-06',
  },
  {
    key: "3",
    name: "Hoàng C",
    price: 912345678,
    hoahong: 50,
    status: true,
    affiliateCode: '6 tháng',
    affiliateLink: 0,
    affiliateAll: 1,
    commission: 50000,
    revenue: '10/08/2025',
    createdAt: '2025-08-10',
  },
  // ... thêm cho đủ total nếu cần demo
];

const TOTAL_ENTRIES = 83; // giả sử dataset tổng là 83 (theo hình mẫu). Bạn có thể thay bằng total từ API.

const PartnerList: React.FC = () => {
  const navigate = useNavigate();
  const [data] = useState<Partner[]>(sampleData);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const searchInputRef = useRef<InputRef | null>(null);

  const handleView = (record: Partner) => {
    navigate("/dich-vu/chi-tiet");
  };

  const handleDelete = (record: Partner) => {
    message.success(`Đã xóa: ${record.name}`);
    // TODO: logic xóa + refresh data
  };

  const renderStatusTag = (status: Partner["status"]) => {
    const color = status === true ? "green" : "red";
    return <Tag color={color}>{status ? "Active" : "UnActive"}</Tag>;
  };

  const getColumnSearchProps = <T extends object>(dataIndex: keyof T): ColumnType<T> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            // @ts-ignore
            searchInputRef.current = node;
          }}
          placeholder={`Tìm ${String(dataIndex)}`}
          value={selectedKeys?.[0] as string}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setSearchText((selectedKeys?.[0] as string) || "");
          }}
          style={{ marginBottom: 8, display: "block", width: 188 }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setSearchText((selectedKeys?.[0] as string) || "");
            }}
            size="small"
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters();
              setSearchText("");
              confirm();
            }}
            size="small"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value: boolean | React.Key, record: T) => {
      const recordValue = (record as any)[dataIndex];
      if (recordValue === undefined || recordValue === null) return false;
      return String(recordValue).toLowerCase().includes(String(value).toLowerCase());
    },
  });

  const columns: ColumnsType<Partner> = [
    {
      title: "Họ và tên",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps<Partner>("name"),
      render: (text: string) => <a href="/dich-vu/chi-tiet" style={{ color: "#6e6b7b" }}>{text}</a>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Giá",
      dataIndex: "price",
      key: "price",
      ...getColumnSearchProps<Partner>("price"),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: "Hoa hồng",
      dataIndex: "hoahong",
      key: "hoahong",
      ...getColumnSearchProps<Partner>("hoahong"),
      sorter: (a, b) => a.hoahong - b.hoahong,
      render: (val: number) => <span>{val}%</span>,
    },
    {
      title: "Thời hạn",
      dataIndex: "affiliateCode",
      key: "affiliateCode",
      sorter: (a, b) => a.affiliateCode.localeCompare(b.affiliateCode),
      align: "center",
    },
    {
      title: "Số lần sử dụng",
      dataIndex: "affiliateLink",
      key: "affiliateLink",
      sorter: (a, b) => a.affiliateLink - b.affiliateLink,
      align: "center",
    },
    {
      title: "Số user",
      dataIndex: "affiliateAll",
      key: "affiliateAll",
      sorter: (a, b) => a.affiliateAll - b.affiliateAll,
      align: "center",
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Đang hoạt động", value: "Đang hoạt động" },
        { text: "Hết hạn", value: "Hết hạn" },
        { text: "Chưa duyệt", value: "Chưa duyệt" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => renderStatusTag(status),
      width: 140,
    },
    {
      title: "Lượt mua",
      dataIndex: "commission",
      key: "commission",
      sorter: (a, b) => a.commission - b.commission,
      align: "right",
      render: (val: number) => <span style={{ color: "#6e6b7b" }}>{val.toLocaleString("vi-VN")}</span>,
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "right",
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (val: string) => (
        <span style={{ color: "#6e6b7b" }}>
          {new Date(val).toLocaleDateString("vi-VN")}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => {
        const items = [
          {
            key: "view",
            label: (
              <span onClick={() => handleView(record)} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <EyeOutlined /> Xem chi tiết
              </span>
            ),
          },
          {
            key: "delete",
            label: (
              <span onClick={() => handleDelete(record)} style={{ display: "flex", alignItems: "center", gap: 8, color: "red" }}>
                <DeleteOutlined /> Xóa
              </span>
            ),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
      width: 80,
    },
  ];

  // compute displayed data (demo): use searchText quick filter for client-side
  const displayedData = searchText
    ? data.filter(
      (r) =>
        r.name.toLowerCase().includes(searchText.toLowerCase())
    )
    : data;

  // For demo "total" (the text "Showing 1 to 10 of 83 entries"), we use TOTAL_ENTRIES,
  // but the actual rows shown in table = displayedData.
  const total = TOTAL_ENTRIES;
  const [currentPage, setCurrentPage] = useState(1);

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(total, currentPage * pageSize);
  return (
    <Layout>
      <div className="partners-page">
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ fontWeight: 600, fontSize: 24, marginBottom: 0, color: "#6e6b7b", borderRight: "1px solid #d6dce1", paddingRight: 12 }}>
            Đối tác
          </h2>
          <Breadcrumb
            separator={<RightOutlined style={{ fontSize: 12, color: "#6e6b7b" }} />}
            items={[
              {
                title: (
                  <a href="#">
                    <HomeOutlined style={{ color: "#096dd9", fontSize: 16 }} />
                  </a>
                ),
              },
              { title: "Danh sách" },
            ]}
          />
        </div>

        <div className="card-like" style={{ borderRadius: 8, border: "1px solid #f0f0f0", background: "#fff" }}>
          {/* Filters top bar */}
          <div style={{ padding: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Select
              defaultValue="10"
              style={{ width: 80, height: 38 }}
              onChange={(v) => {
                setPageSize(Number(v));
                setCurrentPage(1);
              }}
              className="control-38"
            >
              <Option value="10">10</Option>
              <Option value="20">20</Option>
              <Option value="50">50</Option>
            </Select>

            <Select defaultValue="all" style={{ width: 150, height: 38 }} className="control-38">
              <Option value="all">Xem tất cả</Option>
              <Option value="active">Đang hoạt động</Option>
              <Option value="expired">Hết hạn</Option>
              <Option value="pending">Chưa duyệt</Option>
            </Select>

            <Input
              placeholder="Tìm nhanh theo tên/phone/email"
              prefix={<SearchOutlined />}
              style={{ width: 320, height: 38 }}
              onPressEnter={(e) => {
                const v = (e.target as HTMLInputElement).value.trim();
                setSearchText(v);
                setCurrentPage(1);
              }}
              className="control-38"
            />

            <Button className="cl-white" type="primary" icon={<ExportOutlined style={{ color: "#fff" }} />} style={{ marginLeft: "auto", height: 38 }}>
              Export
            </Button>
          </div>

          {/* Table */}
          <Table
            className="custom-table"
            columns={columns}
            dataSource={displayedData}
            pagination={false}
            rowKey="key"
            size="middle"
            bordered={false}
            scroll={{ x: '1200px' }}
          />

          {/* Pagination area: left info + right pagination */}
          <div className="partners-pagination" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, padding: '0 16px 16px 16px' }}>
            <div className="info-text">
              Showing {start} to {end} of {total} entries
            </div>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              className="custom-pagination"
            />
          </div>
        </div>
      </div>
    </Layout >
  );
};

export default PartnerList;
